import { useState, useEffect, useCallback } from "react";
import { UserCircle, Save, Key, CreditCard, Crown, Loader2, Check, XCircle, ArrowLeftRight, RotateCcw, CalendarClock, ExternalLink, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Layout from "@/components/Layout";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function ProfilePage({ onLogout }) {
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPwDialog, setShowPwDialog] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [changeTo, setChangeTo] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const [profRes, subRes] = await Promise.all([
        axios.get(`${API}/api/profile`, { headers: getHeaders() }),
        axios.get(`${API}/api/subscription/status`, { headers: getHeaders() }),
      ]);
      setProfile(profRes.data);
      setName(profRes.data.name || "");
      setEmail(profRes.data.email || "");
      setSubscription(subRes.data);
    } catch { toast.error("Fehler beim Laden"); }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/profile`, { name, email }, { headers: getHeaders() });
      setProfile(res.data);
      localStorage.setItem("user", JSON.stringify({ name: res.data.name, email: res.data.email }));
      toast.success("Profil gespeichert");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setSaving(false);
  };

  const handleChangePw = async () => {
    setPwLoading(true);
    try {
      await axios.post(`${API}/api/profile/change-password`, { current_password: currentPw, new_password: newPw }, { headers: getHeaders() });
      toast.success("Passwort geändert");
      setShowPwDialog(false);
      setCurrentPw("");
      setNewPw("");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setPwLoading(false);
  };

  const handleBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/api/profile/billing-portal`, {}, { headers: getHeaders() });
      window.open(res.data.url, "_blank");
    } catch (err) { toast.error(err.response?.data?.detail || "Kein Stripe-Konto verknüpft"); }
    setPortalLoading(false);
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/subscription/cancel`, {}, { headers: getHeaders() });
      toast.success("Abonnement wird zum Ende der Laufzeit gekündigt");
      setShowCancelDialog(false);
      loadProfile();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setActionLoading(false);
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/subscription/reactivate`, {}, { headers: getHeaders() });
      toast.success("Kündigung zurückgenommen");
      loadProfile();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setActionLoading(false);
  };

  const handleChangePlan = async () => {
    if (!changeTo) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/subscription/change-plan?plan=${changeTo}`, {}, { headers: getHeaders() });
      toast.success(`Plan gewechselt zu ${changeTo === "premium" ? "Premium" : "Basic"}`);
      setShowChangeDialog(false);
      loadProfile();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setActionLoading(false);
  };

  const handleSubscribe = async (plan) => {
    try {
      const res = await axios.post(`${API}/api/subscription/checkout`, { plan, billing: "monthly", origin_url: window.location.origin }, { headers: getHeaders() });
      window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  if (!profile) return <Layout onLogout={onLogout}><div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-slate-300" /></div></Layout>;

  const isActive = subscription?.status === "active";
  const isCanceling = subscription?.cancel_at_period_end;
  const isPastDue = subscription?.status === "past_due";
  const isAdmin = subscription?.is_admin;
  const currentPlan = subscription?.plan;
  const periodEnd = subscription?.current_period_end;

  return (
    <Layout onLogout={onLogout}>
      <div className="h-full overflow-y-auto p-6 bg-slate-50/50" data-testid="profile-page">
        <div className="max-w-2xl mx-auto space-y-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2.5">
            <UserCircle className="w-6 h-6 text-slate-400" /> Profil
          </h1>

          {/* Personal Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="profile-info-card">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Persönliche Daten</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Name</label>
                <Input value={name} onChange={e => setName(e.target.value)} data-testid="profile-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">E-Mail</label>
                <Input value={email} onChange={e => setEmail(e.target.value)} type="email" data-testid="profile-email" />
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={handleSave} disabled={saving} size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="profile-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Speichern
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowPwDialog(true)} data-testid="change-password-btn">
                  <Key className="w-4 h-4 mr-1.5" /> Passwort ändern
                </Button>
              </div>
            </div>
          </div>

          {/* Subscription Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="profile-subscription-card">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Abonnement</h2>

            {isAdmin && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <Crown className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-sm font-bold text-amber-800">Admin — Vollzugang</p>
                  <p className="text-xs text-amber-600">Alle Funktionen freigeschaltet ohne Abonnement</p>
                </div>
              </div>
            )}

            {isActive && !isCanceling && !isAdmin && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg mb-4" data-testid="profile-sub-active">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-sm font-bold text-emerald-800">{currentPlan === "premium" ? "Premium" : "Basic"} — Aktiv</p>
                      {periodEnd && <p className="text-xs text-emerald-600 flex items-center gap-1 mt-0.5"><CalendarClock className="w-3 h-3" /> Nächste Zahlung: {new Date(periodEnd).toLocaleDateString("de-DE")}</p>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  {currentPlan === "basic" && (
                    <Button size="sm" onClick={() => { setChangeTo("premium"); setShowChangeDialog(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="profile-upgrade-btn">
                      <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" /> Upgrade zu Premium
                    </Button>
                  )}
                  {currentPlan === "premium" && (
                    <Button size="sm" variant="outline" onClick={() => { setChangeTo("basic"); setShowChangeDialog(true); }} data-testid="profile-downgrade-btn">
                      <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" /> Downgrade zu Basic
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowCancelDialog(true)} data-testid="profile-cancel-btn">
                    <XCircle className="w-3.5 h-3.5 mr-1.5" /> Kündigen
                  </Button>
                </div>
              </div>
            )}

            {isCanceling && !isAdmin && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4" data-testid="profile-sub-canceling">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-amber-800">{currentPlan === "premium" ? "Premium" : "Basic"} — Wird gekündigt</p>
                    {periodEnd && <p className="text-xs text-amber-600">Zugang bis: {new Date(periodEnd).toLocaleDateString("de-DE")}</p>}
                  </div>
                  <Button size="sm" onClick={handleReactivate} disabled={actionLoading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="profile-reactivate-btn">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Zurücknehmen</>}
                  </Button>
                </div>
              </div>
            )}

            {isPastDue && !isAdmin && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-sm font-bold text-red-800">Zahlung ausstehend — Bitte Zahlungsmethode aktualisieren</p>
                </div>
              </div>
            )}

            {!isActive && !isPastDue && !isCanceling && !isAdmin && (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg mb-4" data-testid="profile-no-sub">
                <p className="text-sm text-slate-600 mb-3">Kein aktives Abonnement.</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleSubscribe("basic")}>Basic — €14/Monat</Button>
                  <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => handleSubscribe("premium")}>Premium — €19/Monat</Button>
                </div>
              </div>
            )}

            {/* Billing Portal */}
            <Button variant="outline" size="sm" onClick={handleBillingPortal} disabled={portalLoading} data-testid="billing-portal-btn" className="w-full">
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
              Zahlungsmethode verwalten
              <ExternalLink className="w-3 h-3 ml-1.5 text-slate-400" />
            </Button>
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Konto</h2>
            <div className="text-xs text-slate-400 space-y-1">
              <p>ID: {profile.id}</p>
              {profile.created_at && <p>Erstellt: {new Date(profile.created_at).toLocaleDateString("de-DE")}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Password Dialog */}
      <Dialog open={showPwDialog} onOpenChange={setShowPwDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Passwort ändern</DialogTitle>
            <DialogDescription>Geben Sie Ihr aktuelles und neues Passwort ein.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input type="password" placeholder="Aktuelles Passwort" value={currentPw} onChange={e => setCurrentPw(e.target.value)} data-testid="current-password" />
            <Input type="password" placeholder="Neues Passwort" value={newPw} onChange={e => setNewPw(e.target.value)} data-testid="new-password" />
            <Button onClick={handleChangePw} disabled={pwLoading || !currentPw || !newPw} className="w-full" data-testid="confirm-password-btn">
              {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Passwort ändern"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-700">Abonnement kündigen?</DialogTitle>
            <DialogDescription>
              Ihr Zugang bleibt bis zum Ende der Laufzeit bestehen{periodEnd ? ` (${new Date(periodEnd).toLocaleDateString("de-DE")})` : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowCancelDialog(false)}>Abbrechen</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleCancel} disabled={actionLoading} data-testid="confirm-cancel-profile">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ja, kündigen"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={showChangeDialog} onOpenChange={setShowChangeDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Plan wechseln zu {changeTo === "premium" ? "Premium" : "Basic"}?</DialogTitle>
            <DialogDescription>
              {changeTo === "premium" ? "Sofort Zugang zu allen Premium-Funktionen. Differenz wird anteilig berechnet." : "Wechsel zum Basic-Plan. Preisunterschied wird gutgeschrieben."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowChangeDialog(false)}>Abbrechen</Button>
            <Button className="flex-1 bg-slate-900 hover:bg-slate-800" onClick={handleChangePlan} disabled={actionLoading} data-testid="confirm-change-profile">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Bestätigen"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
